import React, { useState, useEffect } from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
  alpha,
  Card,
  CardContent,
  Badge,
  Chip,
  Fade,
  Zoom,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import { useTheme as useThemeMui } from "@mui/material/styles";
import {
  Kitchen,
  CheckCircle,
  Warning,
  LocalDining,
  Restaurant,
  Star,
  Whatshot,
} from "@mui/icons-material";
import nutripot from "../../Assets/Modules/nutripot.png";

// Enhanced pot configuration with better visuals
const POT_CONFIG = {
  np1: {
    id: "np1",
    name: "Pot 1",
    shortName: "Pot 1",
    icon: <Restaurant />,
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#667eea",
    description: "Primary cooking pot",
  },
  np2: {
    id: "np2",
    name: "Pot 2",
    shortName: "Pot 2",
    icon: <LocalDining />,
    gradient: "linear-gradient(135deg,rgb(149, 56, 95) 0%, #f5576c 100%)",
    color: "#f093fb",
    description: "Secondary cooking pot",
  },
};

const NutriPotSelector = ({ value, onChange, disabledValue, sx }) => {
  const theme = useTheme();
  const [selectedNutriPot, setSelectedNutriPot] = useState(value || "np1");
  const [hoveredPot, setHoveredPot] = useState(null);

  useEffect(() => {
    if (value) {
      setSelectedNutriPot(value);
    }
  }, [value]);

  // const handleChange = (event, newValue) => {
  //   // If nothing is selected (user clicked the already selected button), maintain current selection
  //   if (newValue === null) return;

  //   // Update the state and call the onChange handler
  //   setSelectedNutriPot(newValue);
  //   onChange(newValue);
  // };

  const isDisabled = (potValue) => {
    return disabledValue === potValue;
  };

  const handlePotClick = (potId) => {
    if (!isDisabled(potId)) {
      setSelectedNutriPot(potId);
      onChange(potId);
    }
  };

  return (
    <Box sx={{ width: "100%", ...sx }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Kitchen sx={{ color: "primary.main", fontSize: 28 }} />
        <Typography
          variant="h6"
          fontWeight="bold"
          color="text.primary"
          sx={{
            background: "linear-gradient(45deg, #667eea, #764ba2)",
            backgroundClip: "text",
            textFillColor: "transparent",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Select which pot we are using for this recipe
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          "@media (max-width: 600px)": {
            gridTemplateColumns: "1fr",
            gap: 1.5,
          },
        }}
      >
        {Object.values(POT_CONFIG).map((pot) => {
          const isSelected = selectedNutriPot === pot.id;
          const disabled = isDisabled(pot.id);
          const isHovered = hoveredPot === pot.id;

          return (
            <Zoom
              key={pot.id}
              in
              timeout={300}
              style={{ transitionDelay: pot.id === "np1" ? "0ms" : "150ms" }}
            >
              <Card
                onClick={() => handlePotClick(pot.id)}
                onMouseEnter={() => setHoveredPot(pot.id)}
                onMouseLeave={() => setHoveredPot(null)}
                sx={{
                  position: "relative",
                  cursor: disabled ? "not-allowed" : "pointer",
                  height: "200px",
                  borderRadius: "16px",
                  overflow: "hidden",
                  background: disabled
                    ? theme.palette.grey[100]
                    : isSelected
                    ? pot.gradient
                    : "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  border: isSelected
                    ? `3px solid ${pot.color}`
                    : disabled
                    ? `2px solid ${theme.palette.grey[300]}`
                    : "2px solid transparent",
                  transform: disabled
                    ? "none"
                    : isSelected
                    ? "scale(1.05)"
                    : isHovered
                    ? "scale(1.02)"
                    : "scale(1)",
                  transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  boxShadow: disabled
                    ? 1
                    : isSelected
                    ? `0 8px 32px ${alpha(pot.color, 0.4)}`
                    : isHovered
                    ? `0 6px 24px ${alpha(pot.color, 0.3)}`
                    : `0 4px 16px ${alpha("#000", 0.1)}`,
                  "&::before": !disabled && {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: isSelected ? "none" : pot.gradient,
                    opacity: isHovered ? 0.8 : 0,
                    transition: "opacity 0.3s ease",
                  },
                  opacity: disabled ? 0.6 : 1,
                }}
              >
                {/* Status Badge */}
                {(isSelected || disabled) && (
                  <Badge
                    badgeContent={
                      <Chip
                        icon={
                          disabled ? (
                            <Warning fontSize="small" />
                          ) : (
                            <CheckCircle fontSize="small" />
                          )
                        }
                        label={disabled ? "In Use" : "Selected"}
                        size="small"
                        color={disabled ? "error" : "success"}
                        sx={{
                          height: "24px",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                        }}
                      />
                    }
                    sx={{
                      position: "absolute",
                      top: 15,
                      right: 45,
                      zIndex: 2,
                    }}
                  >
                    <Box />
                  </Badge>
                )}

                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 1,
                    p: 2,
                  }}
                >
                  {/* Pot Icon/Image */}
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: disabled
                        ? theme.palette.grey[200]
                        : isSelected
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(255, 255, 255, 0.9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      border: `2px solid ${
                        disabled
                          ? theme.palette.grey[400]
                          : "rgba(255, 255, 255, 0.8)"
                      }`,
                      transition: "all 0.3s ease",
                      transform:
                        isHovered && !disabled ? "scale(1.1)" : "scale(1)",
                    }}
                  >
                    <img
                      src={nutripot}
                      alt={pot.name}
                      style={{
                        width: "60px",
                        height: "60px",
                        filter: disabled
                          ? "grayscale(100%)"
                          : isSelected
                          ? "brightness(1.1)"
                          : "none",
                        transition: "filter 0.3s ease",
                      }}
                    />
                  </Box>

                  {/* Pot Name */}
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      color: disabled
                        ? theme.palette.text.disabled
                        : isSelected
                        ? "white"
                        : "text.primary",
                      mb: 0.5,
                      textAlign: "center",
                      transition: "color 0.3s ease",
                    }}
                  >
                    {pot.shortName}
                  </Typography>

                  {/* Pot Description */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: disabled
                        ? theme.palette.text.disabled
                        : isSelected
                        ? "rgba(255, 255, 255, 0.9)"
                        : "text.secondary",
                      textAlign: "center",
                      fontWeight: 500,
                      transition: "color 0.3s ease",
                    }}
                  >
                    {disabled ? "In use by previous recipe" : pot.description}
                  </Typography>

                  {/* Selection Indicator */}
                  {isSelected && !disabled && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 5,
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "white",
                      }}
                    >
                      <Star sx={{ fontSize: 16 }} />
                      <Typography variant="caption" fontWeight="bold">
                        Active
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                {/* Animated Glow Effect for Selected */}
                {isSelected && !disabled && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -2,
                      left: -2,
                      right: -2,
                      bottom: -2,
                      borderRadius: "18px",
                      background: pot.gradient,
                      opacity: 0.6,
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%": { opacity: 0.6, transform: "scale(1)" },
                        "50%": { opacity: 0.8, transform: "scale(1.02)" },
                        "100%": { opacity: 0.6, transform: "scale(1)" },
                      },
                      zIndex: -1,
                    }}
                  />
                )}
              </Card>
            </Zoom>
          );
        })}
      </Box>

      {/* Selection Summary */}
      <Fade in timeout={500}>
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: "12px",
            background: `linear-gradient(135deg, ${alpha(
              POT_CONFIG[selectedNutriPot]?.color || "#667eea",
              0.1
            )}, ${alpha(
              POT_CONFIG[selectedNutriPot]?.color || "#764ba2",
              0.05
            )})`,
            border: `1px solid ${alpha(
              POT_CONFIG[selectedNutriPot]?.color || "#667eea",
              0.2
            )}`,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Whatshot
            sx={{
              color: POT_CONFIG[selectedNutriPot]?.color || "primary.main",
              fontSize: 24,
            }}
          />
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="text.primary"
            >
              Selected: {POT_CONFIG[selectedNutriPot]?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {POT_CONFIG[selectedNutriPot]?.description}
            </Typography>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};

NutriPotSelector.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabledValue: PropTypes.string,
  sx: PropTypes.object,
};

export default NutriPotSelector;
